import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Inbox, Loader2, Plus, Sparkles } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { isGuest } from "@/lib/guest";
import { themeVars } from "@/lib/themes";
import { useSpace } from "@/context/SpaceContext";
import { VisibilityIcon, visibilityOf } from "@/components/library/VisibilityIcon";
import { NewAlbumDialog } from "@/components/library/NewAlbumDialog";
import { albumMeta } from "@/components/book/ViewToggle";
import { DailyBackdrop } from "@/components/theme/DailyBackdrop";

const PAGE = 24;

const AlbumCard = ({ a }) => {
  const t = a.theme;
  const themed = t && t.key !== "classic" && t.bg_thumb;
  return (
    <Link to={`/albums/${a.id}`} className={`book-page book-cover album-link ${themed ? "themed-card" : ""}`} style={t && t.key !== "classic" ? themeVars(t) : undefined}
      data-testid={`album-card-${a.id}`} data-theme={t?.key}>
      {themed && <img className="card-bg" src={mediaUrl(t.bg_thumb)} alt="" loading="lazy" aria-hidden data-testid={`album-card-bg-${a.id}`} />}
      <div className="card-img">
        {a.cover_url ? <img className="card-cover" src={mediaUrl(a.cover_url)} alt="" loading="lazy" /> : <span className="card-cover card-cover-empty" />}
        <div className="card-tags">
          {a.unsorted && <span className="smart-badge unsorted-badge" data-testid="unsorted-badge"><Inbox size={10} /> Unsorted</span>}
          {a.yearbook && <span className="smart-badge yb-badge" data-testid={`yearbook-badge-${a.id}`}><BookOpen size={10} /> Year Book</span>}
          {a.smart && <span className="smart-badge" data-testid={`smart-album-badge-${a.id}`}><Sparkles size={10} /> Smart</span>}
          {!isGuest() && <VisibilityIcon space={visibilityOf(a.spaces || [a.space])} className="card-vis" testid={`album-visibility-${a.id}`} />}
        </div>
      </div>
      <div className="card-caption">
        <span className="card-name" title={a.name} data-testid={`album-card-name-${a.id}`}>{a.name}</span>
        <span className="card-rule" aria-hidden />
        <span className="card-meta" data-testid={`album-card-meta-${a.id}`}>{albumMeta(a)}</span>
      </div>
    </Link>
  );
};

const useAlbumPages = (space) => {
  const [albums, setAlbums] = useState([]);
  const [cursor, setCursor] = useState("0");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fetchFrom = useCallback(async (from, reset) => {
    setLoading(true);
    try {
      const { data } = await api.get("/albums", { params: { space, cursor: from, limit: PAGE } });
      setAlbums((prev) => (reset ? data.albums : [...prev, ...data.albums]));
      setCursor(data.next_cursor);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [space]);
  useEffect(() => { setAlbums([]); setLoaded(false); fetchFrom("0", true); }, [fetchFrom]);
  const more = useCallback(() => { if (cursor && !loading) fetchFrom(cursor, false); }, [cursor, loading, fetchFrom]);
  return { albums, more, hasMore: Boolean(cursor), loading, loaded, reload: () => fetchFrom("0", true) };
};

export default function AlbumsPage() {
  const { space } = useSpace();
  const { albums, more, hasMore, loading, loaded, reload } = useAlbumPages(space);
  const [sentinel, setSentinel] = useState(null);

  useEffect(() => {
    if (!sentinel || !hasMore) return undefined;
    const obs = new IntersectionObserver((e) => e[0].isIntersecting && more(), { rootMargin: "800px" });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [sentinel, hasMore, more]);

  const [creating, setCreating] = useState(false);
  const books = albums.filter((a) => a.yearbook).sort((a, b) =>
    (a.yearbook_kind === "birthday") - (b.yearbook_kind === "birthday") || (a.yearbook_kind === "birthday" ? a.name.localeCompare(b.name) : 0));
  const regular = albums.filter((a) => !a.smart && !a.yearbook && !a.unsorted);
  const unsorted = albums.find((a) => a.unsorted);
  const smart = albums.filter((a) => a.smart);

  return (
    <div className="daily-home" data-testid="albums-page">
      <DailyBackdrop />
      <div className="view-toolbar">
        {!isGuest() && <button className="tool-btn" onClick={() => setCreating(true)} data-testid="create-album-button"><Plus size={16} /> <span>New album</span></button>}
      </div>
      <NewAlbumDialog open={creating} onClose={() => setCreating(false)} onCreated={reload} />
      {unsorted && <div className="book-grid mb-10" data-testid="unsorted-album-grid"><AlbumCard a={unsorted} /></div>}
      {books.length > 0 && <div className="book-grid mb-10" data-testid="yearbook-albums-grid">{books.map((a) => <AlbumCard key={a.id} a={a} />)}</div>}
      {regular.length > 0 && <div className="book-grid" data-testid="regular-albums-grid">{regular.map((a) => <AlbumCard key={a.id} a={a} />)}</div>}
      {loaded && !regular.length && !books.length && !hasMore && (
        <div className="book-empty" data-testid="albums-empty">
          No albums yet. <Link to="/library" className="underline" data-testid="albums-empty-library-link">Open your library</Link>
        </div>
      )}
      {smart.length > 0 && (
        <section className="mt-12" data-testid="smart-albums-section">
          <h2 className="book-month">Smart albums</h2>
          <div className="book-grid" data-testid="smart-albums-grid">{smart.map((a) => <AlbumCard key={a.id} a={a} />)}</div>
        </section>
      )}
      <div ref={setSentinel} className="h-px" data-testid="albums-sentinel" />
      {loading && <div className="flex justify-center py-10 text-white/80" data-testid="albums-loading"><Loader2 className="animate-spin" /></div>}
    </div>
  );
}
